import { Input } from '@ben/orion/input';
import InputLabelWithHint from '@ben/orion/input-label/input-label-with-hint';
import RadioButton from '@ben/orion/radio-button';
import type {
  IModularBenefitDetail,
  IModularBenefitMessages,
} from '@ben/shared/types/columbia';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import styled, { css } from 'styled-components';
import { fieldCannotBeBlankErrorMessage } from '../../shared/constants';
import ModelDropdown from '../model-select/model-dropdown';

export enum CTALinkType {
  Fixed = 'fixed',
  OutboundSSO = 'outbound-sso',
}

const CTA_LINK_TYPE_OPTIONS: { label: string; value: CTALinkType }[] = [
  { label: 'Fixed', value: CTALinkType.Fixed },
  { label: 'Outbound SSO', value: CTALinkType.OutboundSSO },
];

interface CTALinkProps {
  errors: Record<string, boolean>;
  successes: IModularBenefitMessages;
  type: CTALinkType;
  onTypeChange: (value: CTALinkType) => void;
}

const CTALink = ({ errors, successes, type, onTypeChange }: CTALinkProps) => {
  const { control, setValue } = useFormContext<IModularBenefitDetail>();
  const outboundProvider = useWatch({
    control,
    name: 'outboundProvider',
    exact: true,
  });

  useEffect(() => {
    if (outboundProvider && type !== CTALinkType.OutboundSSO) {
      onTypeChange(CTALinkType.OutboundSSO);
    }
  }, [onTypeChange, outboundProvider, type]);

  const handleTypeChange = (value: CTALinkType) => {
    onTypeChange(value);
    if (value === CTALinkType.Fixed) {
      setValue('outboundProvider', null, { shouldDirty: true });
    }
    if (value === CTALinkType.OutboundSSO) {
      setValue('externalLinkUrl', '', { shouldDirty: true });
    }
  };

  return (
    <Wrapper>
      <TypeSelectionContainer>
        <InputLabelWithHint
          name="CTA link type"
          showLabel
          inputId="cta-link-type"
        />
        <TypeSelection>
          {CTA_LINK_TYPE_OPTIONS.map(({ label, value }) => (
            <RadioButton
              key={value}
              label={label}
              value={value}
              checked={type === value}
              onChange={() => handleTypeChange(value)}
              name="cta-link-type"
            />
          ))}
        </TypeSelection>
      </TypeSelectionContainer>
      {type === CTALinkType.Fixed && (
        <Controller
          name="externalLinkUrl"
          control={control}
          rules={{
            required: false,
            pattern: {
              value:
                /^(https?:\/\/)?[\w#%+.:=@~-]{2,256}\.[a-z]{2,6}\b([\w#%&+./:=?@~-]*)$/i,
              message: "Invalid link. Expected format: 'https://domain.com'",
            },
          }}
          render={({ field, fieldState: { isDirty, error } }) => {
            const { value, ...rest } = field;
            return (
              <Input
                {...rest}
                hasError={!!error || !!errors?.['externalLinkUrl']}
                errorMessage={
                  error?.message ||
                  "Invalid link. Expected format: 'https://domain.com'"
                }
                name="CTA link (optional)"
                hintText="Expected format: ‘https://domain.com’"
                value={value ?? ''}
                width="100%"
                successMessage={isDirty ? undefined : successes.externalLinkUrl}
              />
            );
          }}
        />
      )}
      {type === CTALinkType.OutboundSSO && (
        <Controller
          name="outboundProvider"
          control={control}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
          }}
          render={({ field, fieldState: { isDirty, error } }) => {
            const { value, onChange, ref } = field;
            return (
              <ModelDropdown
                key={value}
                ref={ref}
                name="Provider"
                apiName="outbound-providers"
                selected={Number(value)}
                onChange={onChange}
                hasError={!!error || !!errors?.['outboundProvider']}
                errorMessage={fieldCannotBeBlankErrorMessage}
                successMessage={
                  isDirty ? undefined : successes.outboundProvider
                }
                width="100%"
              />
            );
          }}
        />
      )}
    </Wrapper>
  );
};

export default CTALink;

const Wrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const TypeSelectionContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
  `}
`;

const TypeSelection = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;
